import { useCallback, useEffect, useState } from "react";
import { App, Button, Card, Descriptions, Drawer, Empty, Input, Select, Space, Table, Tabs, Tag, Typography } from "antd";
import { adminFetch, errorMessage } from "../../lib/adminApi";
import type {
  DeliveriesResponse,
  DeliveryDetail,
  DeliveryDetailResponse,
  DeliveryRow,
  DeliveryStatus,
} from "../../types/notifications";
import { DELIVERY_STATUS_COLORS, formatDateTime } from "../../types/notifications";
import { CHANNEL_LABELS, NOTIFICATION_TYPE_LABELS } from "../../types/filterAdmin";
import { ActiveSubscriptionSwitch } from "../../components/ActiveSubscriptionSwitch";

const { Title, Paragraph, Text } = Typography;

const STATUS_OPTIONS: DeliveryStatus[] = ["pending", "sending", "sent", "failed", "bounced", "expired"];
const RETRYABLE: DeliveryStatus[] = ["failed", "bounced", "expired"];

function StatusTag({ status }: { status: DeliveryStatus }) {
  return <Tag color={DELIVERY_STATUS_COLORS[status] ?? "default"}>{status}</Tag>;
}

/**
 * Treść maila w odizolowanym iframe: pusty `sandbox` blokuje skrypty, formularze i
 * nawigację — tytuły i nazwy zamawiających pochodzą z zewnętrznych portali.
 */
function MailPreview({ html }: { html: string }) {
  return (
    <iframe
      title="Treść wysyłki"
      sandbox=""
      srcDoc={html}
      style={{ width: "100%", height: 640, border: "1px solid #f0f0f0", borderRadius: 8 }}
    />
  );
}

function DetailView({ detail }: { detail: DeliveryDetail }) {
  const { delivery, recipient } = detail;
  const bodyMissing = !delivery.body_html && !delivery.body_text;
  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label="Status">
          <StatusTag status={delivery.status} /> próby: {delivery.attempts}
        </Descriptions.Item>
        {delivery.last_error && (
          <Descriptions.Item label="Ostatni błąd">
            <Text type="danger">{delivery.last_error}</Text>
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Temat">{delivery.subject ?? "—"}</Descriptions.Item>
        <Descriptions.Item label="Kanał / rodzaj">
          {delivery.channel} · {delivery.kind} ·{" "}
          {delivery.notification_types.map((t) => NOTIFICATION_TYPE_LABELS[t] ?? t).join(", ") || "—"}
        </Descriptions.Item>
        {delivery.window_start && (
          <Descriptions.Item label="Okno">
            {formatDateTime(delivery.window_start)} → {formatDateTime(delivery.window_end)}
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Utworzona / wysłana">
          {formatDateTime(delivery.created_at)} → {formatDateTime(delivery.sent_at)}
        </Descriptions.Item>
        <Descriptions.Item label="Id dostawcy">{delivery.provider_message_id ?? "—"}</Descriptions.Item>

        <Descriptions.Item label="Odbiorca">
          <Space direction="vertical" size={0}>
            <Text>{recipient.email ?? "brak e-maila"}</Text>
            <Text type="secondary">{recipient.phone ?? "brak telefonu"}</Text>
            <Text type="secondary" copyable={{ text: recipient.user_id }}>
              {recipient.user_id}
            </Text>
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label="Kanały odbiorcy">
          {recipient.channels.length === 0 ? (
            "—"
          ) : (
            <Space wrap>
              {recipient.channels.map((c) => (
                <Tag key={`${c.type}-${c.method}`} color={c.enabled ? "green" : "default"}>
                  {NOTIFICATION_TYPE_LABELS[c.type] ?? c.type}: {CHANNEL_LABELS[c.method] ?? c.method} {c.enabled ? "" : "(wył.)"}
                </Tag>
              ))}
            </Space>
          )}
        </Descriptions.Item>
      </Descriptions>

      <Tabs
        items={[
          {
            key: "html",
            label: "Treść HTML",
            children: delivery.body_html ? (
              <MailPreview html={delivery.body_html} />
            ) : (
              <Empty
                description={
                  bodyMissing
                    ? delivery.body_purged_at
                      ? `Treść usunięta przez retencję (${formatDateTime(delivery.body_purged_at)}).`
                      : "Treść jeszcze nie wyrenderowana — wysyłka nie przeszła przez sendera."
                    : "Ta wysyłka nie ma wersji HTML (np. SMS)."
                }
              />
            ),
          },
          {
            key: "text",
            label: "Treść tekstowa",
            children: delivery.body_text ? (
              <pre style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>{delivery.body_text}</pre>
            ) : (
              <Empty description="Brak wersji tekstowej." />
            ),
          },
          {
            key: "items",
            label: `Sekcje (${detail.items.length})`,
            children: (
              <Table
                rowKey="filter_id"
                size="small"
                pagination={false}
                dataSource={detail.items}
                expandable={{
                  expandedRowRender: (item) => (
                    <Space direction="vertical" size={4}>
                      {[...item.new_payloads.map((a) => ({ a, change: false })), ...item.change_payloads.map((a) => ({ a, change: true }))].map(
                        ({ a, change }) => (
                          <Text key={`${change}-${a.pretty_id}`}>
                            {change ? <Tag color="orange">zmiana</Tag> : <Tag color="blue">nowy</Tag>}
                            {a.pretty_id} — {a.subject ?? "(bez tytułu)"}
                          </Text>
                        ),
                      )}
                    </Space>
                  ),
                }}
                columns={[
                  { title: "Filtr", dataIndex: "filter_name", key: "filter_name" },
                  { title: "Razem", dataIndex: "count", key: "count" },
                  { title: "Nowe / zmiany", key: "split", render: (_: unknown, i) => `${i.new_count} / ${i.change_count}` },
                  { title: "Raport", key: "report", render: (_: unknown, i) => i.report_pretty_id ?? i.report_id ?? "—" },
                ]}
              />
            ),
          },
          {
            key: "notes",
            label: `Notatki (${detail.notes.length})`,
            children: (
              <Table
                rowKey="id"
                size="small"
                pagination={false}
                dataSource={detail.notes}
                columns={[
                  { title: "Przetarg", dataIndex: "auction_pretty_id", key: "auction", render: (v: string | null) => v ?? "—" },
                  { title: "Wpis", dataIndex: "title", key: "title" },
                  { title: "Utworzony", dataIndex: "created_at", key: "created_at", render: formatDateTime },
                ]}
              />
            ),
          },
        ]}
      />
    </Space>
  );
}

export const NotificationDeliveriesList = () => {
  const { notification, modal } = App.useApp();
  const [rows, setRows] = useState<DeliveryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<DeliveryStatus | undefined>();
  const [channel, setChannel] = useState<string | undefined>();
  const [activeOnly, setActiveOnly] = useState(true);
  const [detail, setDetail] = useState<DeliveryDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [retryingId, setRetryingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (query.trim()) params.set("q", query.trim());
    if (status) params.set("status", status);
    if (channel) params.set("channel", channel);
    if (activeOnly) params.set("active_only", "true");
    try {
      const response = await adminFetch<DeliveriesResponse>(`notifications/deliveries?${params.toString()}`);
      setRows(response.data ?? []);
    } catch (error) {
      setRows([]);
      notification.error({ message: "Nie udało się pobrać wysyłek", description: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  }, [activeOnly, channel, notification, query, status]);

  useEffect(() => {
    void load();
    // Tekst wyszukiwania odświeża listę dopiero po Enter/kliknięciu — nie przy każdym znaku.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, channel, activeOnly]);

  const openDetail = useCallback(
    async (id: string) => {
      setDetailLoading(true);
      setDetail(null);
      try {
        const response = await adminFetch<DeliveryDetailResponse>(`notifications/deliveries/${id}`);
        setDetail(response.data);
      } catch (error) {
        notification.error({ message: "Nie udało się pobrać wysyłki", description: errorMessage(error) });
      } finally {
        setDetailLoading(false);
      }
    },
    [notification],
  );

  const retry = useCallback(
    (row: DeliveryRow) => {
      modal.confirm({
        title: "Ponowić wysyłkę?",
        content: `Wysyłka wróci do kolejki i najbliższy przebieg sendera spróbuje ją wysłać (${row.email ?? row.phone ?? row.user_id}).`,
        okText: "Ponów",
        cancelText: "Anuluj",
        onOk: async () => {
          setRetryingId(row.id);
          try {
            await adminFetch(`notifications/deliveries/${row.id}/retry`, { method: "POST" });
            notification.success({ message: "Wysyłka wróciła do kolejki" });
            await load();
          } catch (error) {
            notification.error({ message: "Nie udało się ponowić", description: errorMessage(error) });
          } finally {
            setRetryingId(null);
          }
        },
      });
    },
    [load, modal, notification],
  );

  return (
    <Card>
      <Title level={4}>Wysyłki powiadomień</Title>
      <Paragraph type="secondary">
        Ostatnie wysyłki (do 200). Szukaj po e-mailu odbiorcy, temacie, treści, id dostawcy, id wysyłki albo numerze
        przetargu.
      </Paragraph>
      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          allowClear
          placeholder="e-mail, temat, przetarg…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onSearch={() => void load()}
          style={{ width: 320 }}
        />
        <Select
          allowClear
          placeholder="Status"
          value={status}
          onChange={setStatus}
          options={STATUS_OPTIONS.map((s) => ({ value: s, label: s }))}
          style={{ width: 140 }}
        />
        <Select
          allowClear
          placeholder="Kanał"
          value={channel}
          onChange={setChannel}
          options={[
            { value: "email", label: "e-mail" },
            { value: "sms", label: "SMS" },
          ]}
          style={{ width: 120 }}
        />
        <ActiveSubscriptionSwitch checked={activeOnly} onChange={setActiveOnly} />
        <Button onClick={() => void load()}>Odśwież</Button>
      </Space>
      <Table<DeliveryRow>
        rowKey="id"
        loading={loading}
        dataSource={rows}
        pagination={{ pageSize: 50 }}
        scroll={{ x: true }}
        columns={[
          { title: "Utworzona", dataIndex: "created_at", key: "created_at", render: formatDateTime },
          {
            title: "Odbiorca",
            key: "recipient",
            render: (_: unknown, row) => (
              <Space direction="vertical" size={0}>
                <Text>{row.channel === "sms" ? row.phone ?? "—" : row.email ?? "—"}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {row.channel} · {row.notification_types.map((t) => NOTIFICATION_TYPE_LABELS[t] ?? t).join(", ") || row.kind}
                </Text>
              </Space>
            ),
          },
          { title: "Temat", dataIndex: "subject", key: "subject", ellipsis: true },
          {
            title: "Status",
            key: "status",
            render: (_: unknown, row) => (
              <Space direction="vertical" size={0}>
                <StatusTag status={row.status} />
                {row.last_error && (
                  <Text type="danger" style={{ fontSize: 12 }}>
                    {row.last_error}
                  </Text>
                )}
              </Space>
            ),
          },
          { title: "Sekcje", dataIndex: "item_count", key: "item_count" },
          {
            title: "Akcje",
            key: "actions",
            render: (_: unknown, row) => (
              <Space>
                <Button size="small" onClick={() => void openDetail(row.id)}>
                  Szczegóły
                </Button>
                {RETRYABLE.includes(row.status) && (
                  <Button size="small" loading={retryingId === row.id} onClick={() => retry(row)}>
                    Ponów
                  </Button>
                )}
              </Space>
            ),
          },
        ]}
      />
      <Drawer
        width={900}
        open={detailLoading || detail !== null}
        loading={detailLoading}
        onClose={() => setDetail(null)}
        title="Wysyłka"
        destroyOnHidden
      >
        {detail && <DetailView detail={detail} />}
      </Drawer>
    </Card>
  );
};
